import * as Sentry from '@sentry/vue';

async function lazyLoadSentryIntegrations(replaySampleRate: number, errorReplaySampleRate: number) {
    // don't load on server
    if (!process.client) return;

    // Only load Replay if sample rates are configured
    if (replaySampleRate > 0 || errorReplaySampleRate > 0) {
        const { Replay } = await import('@sentry/vue');
        Sentry.addIntegration(
            new Replay({
                maskAllText: false,
                blockAllMedia: false,
            })
        );
    }
}

function getSentryIntegrations() {
    // don't load on server
    if (!process.client) return [];

    const router = useRouter();
    const browserTracing = new Sentry.BrowserTracing({
        routingInstrumentation: Sentry.vueRouterInstrumentation(router),
    });

    return [browserTracing];
}

export default defineNuxtPlugin({
    name: 'sentry',
    parallel: true,
    async setup(nuxtApp) {
        const vueApp = nuxtApp.vueApp;

        const config = useRuntimeConfig();

        const replaySampleRate = config.public.SENTRY_REPLAY_SAMPLE_RATE as number;
        const errorReplaySampleRate = config.public.SENTRY_ERROR_REPLAY_SAMPLE_RATE as number;

        const sentryConfig: any = {
            app: vueApp,
            dsn: (config.public.SENTRY_DSN_PUBLIC as string) ?? undefined,
            integrations: getSentryIntegrations(),

            // Set tracesSampleRate to 1.0 to capture 100%
            // of transactions for performance monitoring.
            // We recommend adjusting this value in production
            tracesSampleRate: config.public.SENTRY_TRACES_SAMPLE_RATE as number,

            // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
            // tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
        };

        // Only add replay sample rates if they're configured
        if (replaySampleRate > 0 || errorReplaySampleRate > 0) {
            // This sets the sample rate. You may want this to be 100% while
            // in development and sample at a lower rate in production
            sentryConfig.replaysSessionSampleRate = replaySampleRate;

            // If the entire session is not sampled, use the below sample rate to sample
            // sessions when an error occurs.
            sentryConfig.replaysOnErrorSampleRate = errorReplaySampleRate;
        }

        Sentry.init(sentryConfig);

        // Lazy-load the replay integration to reduce bundle size (only if configured)
        lazyLoadSentryIntegrations(replaySampleRate, errorReplaySampleRate);
    },
});
